import { useParams } from "react-router-dom";
import { SEOHead } from "@/components/seo";
import { SmoothLink as Link } from "@/components/navigation";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, ArrowRight, User, BookOpen, MessageCircle } from "lucide-react";
import { format } from "date-fns";
import { useBlogPosts, useBlogPostsByCategory } from "@/hooks/useBlogPosts";
import { useBlogCategories } from "@/hooks/useBlogCategories";
import { LazyImage } from "@/components/common/LazyImage";
import { getOptimizedImageUrl } from "@/lib/image-utils";
import { CategoryFilter } from "@/components/shared/CategoryFilter";
import { useMemo } from "react";
import { AIAgentCard } from "@/components/ai";
import { getWhatsAppLink, COMPANY } from "@/config/company";
import { PageHeader } from "@/components/shared/PageHeader";

/** Static SEO intro copy per blog category — always rendered for crawlers */
const CATEGORY_DESCRIPTIONS: Record<string, { intro: string; sub: string }> = {
  default: {
    intro: "TrustedHeal's medical tourism blog is your authoritative resource for planning healthcare travel to India. Our team of medical professionals and patient coordinators publishes detailed guides covering treatment options, cost comparisons, hospital quality, visa requirements, and patient success stories.",
    sub: "Whether you are researching cardiac surgery, dental implants, orthopaedic procedures, fertility treatments, or wellness retreats, our articles provide the transparent, fact-checked information you need to make confident decisions about your healthcare journey.",
  },
  cardiac: {
    intro: "India is home to world-class cardiac centres performing thousands of successful heart surgeries annually. Our cardiac articles cover bypass surgery (CABG), valve replacement, angioplasty, pacemaker implantation, and heart failure management — with detailed cost comparisons, hospital profiles, and recovery timelines.",
    sub: "Learn how international patients save 70–85% on cardiac procedures at JCI-accredited Indian hospitals without compromising on surgical expertise, ICU standards, or post-operative care quality.",
  },
  dental: {
    intro: "Dental tourism to India is booming, with thousands of international patients saving up to 80% on implants, veneers, crowns, and full-mouth rehabilitation. Our dental blog covers every aspect of treatment planning, from choosing implant brands and verifying NABH clinic credentials to planning your trip and understanding warranties.",
    sub: "Browse expert guides on All-on-4, All-on-6, zirconia veneers, smile makeovers, and zygomatic implants — all tailored to help international patients navigate India's dental tourism landscape safely and affordably. We also provide comprehensive breakdowns on full-mouth restoration costs and timeline expectations to ensure your dental journey is predictable.",
  },
  fertility: {
    intro: "India's fertility clinics achieve success rates comparable to leading IVF centres in the USA and Europe, at a fraction of the cost. Our fertility articles explain IVF protocols, ICSI, egg donation, embryo freezing, and the legal framework governing ART for international patients following India's 2021 regulations.",
    sub: "From choosing an ISAR-accredited clinic to understanding hormone protocols and two-week-wait support, our guides are written to help couples make informed, confident decisions at every stage of their fertility journey.",
  },
  oncology: {
    intro: "Cancer treatment in India combines proton therapy, CyberKnife radiosurgery, robotic surgery, targeted therapy, and immunotherapy — delivered by oncologists trained at global cancer institutes. Our oncology articles compare costs for chemotherapy, radiation, and surgery vs Western prices, and explain how to get a second opinion from Indian specialists before travelling.",
    sub: "We cover bone marrow transplants, liver cancer ablation, prostate robotic surgery, and paediatric oncology, giving patients and caregivers the detailed clinical and logistical information needed for a successful treatment journey.",
  },
  neurology: {
    intro: "India's neurology and neurosurgery centres offer advanced treatments for brain tumours, spinal disorders, epilepsy, Parkinson's disease, and stroke rehabilitation. Our neurology articles cover gamma knife radiosurgery, deep brain stimulation (DBS), minimally invasive spine surgery, and robotic neuro interventions. With state-of-the-art neuro-imaging and dedicated neuro-ICUs, these hospitals ensure the highest standards of patient safety and care.",
    sub: "Discover cost comparisons, hospital specialisations, and patient outcomes data to help you choose the right neuro centre in Delhi, Mumbai, Bangalore, or Chennai for your specific diagnosis. Read our comprehensive guides on what to expect during recovery and how international patient departments assist with every step.",
  },
  ophthalmology: {
    intro: "India excels in eye care, offering LASIK, SMILE, cataract surgery with premium IOLs, retinal detachment repair, glaucoma treatment, and corneal transplants at internationally competitive prices. Our ophthalmology articles guide you through laser vision correction options, lens selection, and post-operative recovery.",
    sub: "We explain the differences between bladeless LASIK, SMILE, ICL implants, and trifocal IOLs, helping you make informed choices about your eye health with trusted Indian eye hospitals.",
  },
  pediatric: {
    intro: "India's leading hospitals provide world-class paediatric care including congenital heart surgery, paediatric oncology, neonatal intensive care, and child neurology. Our articles help parents understand treatment options, cost structures, and support networks available when seeking specialist care for children in India. Specialized pediatric hospitals offer family-centered care, ensuring both the child and parents receive comprehensive support throughout the medical process.",
    sub: "From paediatric cardiac surgery at Narayana Health to neuro-paediatric care at AIIMS and Manipal Hospitals, our guides help families plan safe, well-coordinated medical journeys for young patients. We cover important considerations such as pediatric ICU standards, multi-disciplinary team approaches, and specific accommodation options for families.",
  },
  rehabilitation: {
    intro: "Post-surgical rehabilitation, neurological recovery, and sports injury management are core strengths of India's medical tourism offer. Our rehabilitation articles cover physiotherapy protocols, robotic-assisted rehab, aqua therapy, occupational therapy, and return-to-sport programmes.",
    sub: "Whether you need hip replacement recovery coaching, stroke rehabilitation, or sports physio after ACL reconstruction, our guides outline what to expect from Indian rehab centres and how to plan your extended stay.",
  },
  transplants: {
    intro: "India performs thousands of organ transplants each year, with kidney, liver, heart, and bone marrow transplant success rates comparable to Western centres. Our transplant articles explain living-donor vs deceased-donor procedures, legal requirements, immunosuppression protocols, and cost breakdowns.",
    sub: "Learn about transplant waiting times, donor matching, FRRO requirements for extended stays, and which Indian hospitals lead in liver transplant volumes and one-year graft survival rates.",
  },
  "preventive-care": {
    intro: "Preventive healthcare and wellness check-up packages in India offer comprehensive diagnostics — including full-body scans, cardiac stress tests, cancer markers, and genetic screening — at a fraction of Western prices. Our preventive care articles explain what executive health packages include and how to use your India trip for a full health audit.",
    sub: "Discover how to combine a comprehensive medical check-up with a wellness retreat, Ayurveda detox, or yoga programme for a transformative health optimisation journey in India.",
  },
  "weight-loss": {
    intro: "Bariatric surgery in India — including gastric sleeve, gastric bypass, and mini-bypass — is performed by highly experienced surgeons at accredited hospitals, offering savings of 60–75% compared to USA and UK prices. Our weight-loss articles cover surgical eligibility criteria, expected outcomes, nutrition protocols, and long-term follow-up.",
    sub: "Read real patient stories, understand BMI requirements, and learn how Indian bariatric centres manage international patients through remote pre-op consultations, surgery, and post-operative dietary support. We also detail the importance of multidisciplinary care teams, including dietitians and psychologists, in ensuring long-term weight management success after surgery.",
  },
  "medical-tourism": {
    intro: "TrustedHeal's medical tourism articles are your comprehensive guide to planning healthcare travel to India. We cover visa applications, choosing accredited hospitals, understanding cost estimates, managing travel logistics, and ensuring continuity of care when you return home.",
    sub: "From first consultation to final follow-up, our guides demystify the medical tourism process and help you travel with confidence, backed by the expertise of our patient coordinators and clinical advisors.",
  },
  "cosmetic-surgery": {
    intro: "Cosmetic and plastic surgery in India attracts international patients seeking rhinoplasty, facelifts, liposuction, tummy tucks, breast augmentation, and hair transplants at 50–70% less than UK and USA prices. Our cosmetic surgery articles cover board-certified surgeons, technique comparisons, and realistic recovery expectations.",
    sub: "Browse our guides on FUE hair transplant, mommy makeover packages, blepharoplasty, and body contouring to plan a safe and satisfying cosmetic surgery journey to India.",
  },
  "dental-tourism": {
    intro: "India is among the world's top dental tourism destinations, combining top-tier dental clinics, premium materials, and significant cost savings. Our dental tourism articles guide international patients through clinic selection, treatment planning, travel logistics, and aftercare for procedures ranging from a single crown to full-arch implant restoration.",
    sub: "Learn from patient experiences, clinic comparison guides, and detailed procedure breakdowns to make the most of your dental tourism trip to India.",
  },
};


export default function Blog() {
  const { category } = useParams<{ category?: string }>();
  
  // Fetch all posts or posts by category
  const { data: allPosts = [], isLoading: isLoadingAll } = useBlogPosts();
  const { data: categoryPosts = [], isLoading: isLoadingCategory } = useBlogPostsByCategory(
    category // Passed directly as slug
  );
  
  const { data: categories = [] } = useBlogCategories();
  
  // Use filtered posts if category is selected, otherwise all posts
  const posts = category ? categoryPosts : allPosts;
  const isLoading = category ? isLoadingCategory : isLoadingAll;
  
  // Map categories for the filter component
  const categoryItems = useMemo(() => 
    categories.map((cat) => ({
      slug: cat.slug,
      name: `${cat.name} (${cat.count})`,
    })),
    [categories]
  );

  // Find the display name for the active category
  const activeCategoryName = categories.find(
    (cat) => cat.slug === category
  )?.name;

  const pageTitle = activeCategoryName ? `${activeCategoryName} Articles` : "Medical Tourism Insights";
  const pageDescription = "Expert guides, patient stories, and everything you need to know about medical travel to India.";
  const canonicalUrl = category 
    ? `${COMPANY.website}/blog/category/${category}/`
    : `${COMPANY.website}/blog/`;

  const categoryDesc = CATEGORY_DESCRIPTIONS[category ?? ""] ?? CATEGORY_DESCRIPTIONS.default;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={activeCategoryName ? `${activeCategoryName} Articles | TrustedHeal` : "Medical Tourism Insights | TrustedHeal"}
        description={activeCategoryName
          ? `Expert ${activeCategoryName.toLowerCase()} articles covering costs, hospitals, and patient guides for medical tourism to India.`
          : "Expert guides, patient stories, and everything you need to know about medical travel to India."}
        canonicalPath={category ? `/blog/category/${category}/` : "/blog/"}
        ogType="website"
      />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <PageHeader
            title={activeCategoryName ? `${activeCategoryName} Articles` : "Medical Tourism Insights"}
            description="Expert guides, patient stories, and everything you need to know about medical travel to India"
          />

          {/* Static SEO copy — always visible for crawlers and users */}
          <div className="max-w-3xl mx-auto mb-8 text-center space-y-3">
            <p className="text-muted-foreground leading-relaxed">{categoryDesc.intro}</p>
            <p className="text-muted-foreground leading-relaxed text-sm">{categoryDesc.sub}</p>
          </div>

          {/* Category Filter */}
          <CategoryFilter
            categories={categoryItems}
            activeCategory={category}
            basePath="/blog/category"
            allPath="/blog"
            allLabel="All Articles"
          />

          {/* Featured Post - only show on "All" view */}
          {!category && posts.length > 0 && (
            <Card className="mb-12 overflow-hidden group hover:shadow-hover transition-all duration-300">
              <div className="grid md:grid-cols-2">
                <div className="h-64 md:h-auto overflow-hidden">
                  <LazyImage
                    src={getOptimizedImageUrl(posts[0].hero_image_url, 800)}
                    alt={posts[0].title}
                    width={800}
                    height={450}
                    className="h-full min-h-64 transition-transform duration-500 group-hover:scale-105"
                    aspectRatio="auto"
                    priority
                    fallbackIcon={<BookOpen className="w-24 h-24 text-muted-foreground/30" />}
                    fallbackClassName="bg-gradient-to-br from-primary to-accent"
                  />
                </div>
                <CardContent className="p-8 flex flex-col justify-center">
                  <Badge className="w-fit mb-4">{posts[0].category}</Badge>
                  <Link to={`/blog/${posts[0].slug}/`}>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-secondary mb-4 group-hover:text-primary transition-colors">
                      {posts[0].title}
                    </h2>
                  </Link>
                  <p className="text-muted-foreground mb-6 line-clamp-3">
                    {posts[0].excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{posts[0].author}</span>
                    </div>
                    {posts[0].published_at && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{format(new Date(posts[0].published_at), "MMM d, yyyy")}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{posts[0].read_time_minutes} min read</span>
                    </div>
                  </div>
                  <Link to={`/blog/${posts[0].slug}/`}>
                    <Button className="rounded-full w-fit">
                      Read Article
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </div>
            </Card>
          )}

          {/* Posts Grid */}
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse">
                  <div className="h-48 bg-muted rounded-t-lg" />
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded mb-2 w-3/4" />
                    <div className="h-4 bg-muted rounded w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-secondary mb-2">No articles found</h2>
              <p className="text-muted-foreground">
                {category ? "No articles in this category yet." : "Check back soon for new content."}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Skip first post on "All" view since it's featured */}
              {(category ? posts : posts.slice(1)).map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}/`}>
                  <Card className="group overflow-hidden hover:shadow-hover transition-all duration-300 h-full">
                    <div className="overflow-hidden">
                      <LazyImage
                        src={getOptimizedImageUrl(post.hero_image_url, 400)}
                        alt={post.title}
                        width={400}
                        height={225}
                        className="h-48 transition-transform duration-500 group-hover:scale-105"
                        aspectRatio="auto"
                        fallbackIcon={<BookOpen className="w-12 h-12 text-muted-foreground/30" />}
                      />
                    </div>
                    <CardContent className="p-6">
                      <Badge variant="outline" className="mb-3">{post.category}</Badge>
                      <h2 className="font-display text-lg font-semibold text-secondary mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        {post.published_at && (
                          <span>{format(new Date(post.published_at), "MMM d, yyyy")}</span>
                        )}
                        <span>{post.read_time_minutes} min read</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {/* Context-Aware AI Assistant with WhatsApp */}
          <div className="mt-12 space-y-4">
            <AIAgentCard
              context={activeCategoryName ? `${activeCategoryName} medical tourism articles` : "medical tourism information"}
              title="Ready to Take the Next Step?"
              description={activeCategoryName
                ? `Interested in ${activeCategoryName.toLowerCase()} treatments? I can help you understand costs, find specialists, and start your journey.`
                : "I can answer your questions about treatments, costs, and help you plan your healthcare journey to India."
              }
              quickPrompts={[
                "Get a free consultation",
                "How much will I save?",
                "Connect me with a specialist"
              ]}
            />

            {/* Alternative Contact Options */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 p-4 bg-muted/50 rounded-xl border border-border">
              <p className="text-sm text-muted-foreground text-center sm:text-left">
                Prefer speaking with a human? Reach out to our care team directly.
              </p>
              <div className="flex items-center gap-2">
                <Button asChild variant="outline" size="sm" className="rounded-full">
                  <Link to="/contact/">Contact Us</Link>
                </Button>
                <Button
                  asChild
                  size="sm"
                  className="rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white gap-1.5"
                >
                  <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
